import React from "react";
import Icon from "@mdi/react";
import {
  mdiVideoMarkerOutline,
  mdiSubtitlesOutline,
  mdiVideo,
  mdiMusicNote,
  mdiMovie,
} from "@mdi/js";

import { VTT_FILE_TYPES } from "../../constants/media";

export const formatFilename = (fileName) => {
  if (!fileName) return "";
  const preffix = "[LINK_UPL]_";
  if (fileName.startsWith(preffix)) {
    return fileName.substring(preffix.length);
  }
  return fileName;
};

export const getIconForStreamType = (type) => {
  switch (type) {
    case "video":
      return <Icon path={mdiVideo} size={0.875} />;
    case "audio":
      return <Icon path={mdiMusicNote} size={0.875} />;
    case VTT_FILE_TYPES.SUBTITLES:
      return <Icon path={mdiSubtitlesOutline} size={0.875} />;
    case "thumbnails":
      return <Icon path={mdiMovie} size={0.875} />;
    case VTT_FILE_TYPES.CHAPTERS:
      return <Icon path={mdiVideoMarkerOutline} size={0.875} />;
    default:
      return <Icon path={mdiVideo} size={0.875} />;
  }
};

export const getAvailableQualities = (
  sourceMedia,
  encodingStream,
  encodingStreams,
  encodingPlan,
) => {
  if (encodingStream.type !== "video" && encodingStream.type !== "audio")
    return [];

  if (encodingStream.status !== "untranscoded") return [];

  if (!sourceMedia) return [];

  const sourceStream =
    sourceMedia.info.streams[encodingStream.source_media_stream];
  const sourceQualities = getQualities(
    encodingStream.type,
    getMaxQuality(sourceStream),
  );

  const qualities = [];

  sourceQualities.forEach((quality) => {
    for (let i = 0; i < encodingStreams.length; i++) {
      if (encodingStreams[i].key === encodingStream.key) continue;

      if (encodingStreams[i].type !== encodingStream.type) continue;

      if (
        encodingStream.type === "audio" &&
        encodingStreams[i].source_media_stream !==
          encodingStream.source_media_stream
      )
        continue;

      if (
        encodingStreams[i].quality &&
        encodingStreams[i].quality === quality.value
      )
        return;

      if (
        !encodingStreams[i].type === "audio" &&
        encodingStreams[i].manifest &&
        encodingStreams[i].manifest.quality &&
        encodingStreams[i].manifest.quality === quality.value
      ) {
        return;
      }
    }

    if (encodingPlan?.[`${encodingStream.type}_${quality.value}`] === true)
      qualities.push(quality);
  });

  return qualities;
};

export const getEncodingPlanQualities = (type, maxQuality, encodingPlan) => {
  const encodingPlanQualities = getQualities(type, maxQuality);
  const qualities = [];

  encodingPlanQualities.forEach((quality) => {
    if (encodingPlan?.[`${type}_${quality.value}`] === true)
      qualities.push(quality);
  });

  return qualities;
};

export const getQualities = (type, maxQuality) => {
  switch (type) {
    case "video":
      return getVideoQualities(maxQuality);
    case "audio":
      return getAudioQualities(maxQuality);
    default:
      return [{ value: "default", label: "default" }];
  }
};

const getVideoQualities = (maxQuality) => {
  const qualities = [
    {
      value: "2160",
      label: "4K",
    },
    {
      value: "1080",
      label: "1080p",
    },
    {
      value: "720",
      label: "720p",
    },
    {
      value: "576",
      label: "576p",
    },
    {
      value: "480",
      label: "480p",
    },
    {
      value: "360",
      label: "360p",
    },
  ];

  const qualityIndex = {
    2160: 0,
    1080: 1,
    720: 2,
    576: 3,
    480: 4,
    360: 5,
  };

  return qualities.splice(qualityIndex[maxQuality], 6);
};

const getAudioQualities = (maxQuality) => {
  const qualities = [
    {
      value: "320",
      label: "320kbps",
    },
    {
      value: "160",
      label: "160kbps",
    },
  ];

  const qualityIndex = {
    320: 0,
    160: 1,
  };

  return qualities.splice(qualityIndex[maxQuality], 2);
};

export const getQuality = (quality, manifest) => {
  let q = quality;

  if (typeof q !== "string" && manifest) {
    q = manifest.quality;
  }

  if (typeof q !== "string") return null;

  switch (quality) {
    case "128":
    case "160":
    case "320":
      return {
        value: quality,
        label: `${quality}kbps`,
      };
    case "360":
    case "480":
    case "576":
    case "720":
    case "1080":
      return {
        value: quality,
        label: `${quality}p`,
      };
    case "2160":
      return {
        value: quality,
        label: "4K",
      };
    default:
      return {
        value: quality,
        label: quality,
      };
  }
};

const getMaxQuality = (sourceStream) => {
  switch (sourceStream.codec_type) {
    case "video":
      return getVideoQuality(sourceStream.height);
    case "audio":
      return getAudioQuality(sourceStream.bit_rate);
    default:
      return null;
  }
};

export const getAudioQuality = (bitRate) => {
  if (parseInt(bitRate, 10) <= 160000) {
    return "160";
  }

  return "320";
};

export const getVideoQuality = (height) => {
  switch (true) {
    case height >= 2160:
      return "2160";
    case height >= 1080:
      return "1080";
    case height >= 720:
      return "720";
    case height >= 576:
      return "576";
    case height >= 480:
      return "480";
    case height >= 360:
      return "360";
    default:
      return "360";
  }
};

export const canAddStream = (
  sourceMedia,
  sourceStream,
  encodingStreams,
  encodingPlan,
  currentMedia,
) => {
  const qualitiesAvg = getAvailableQualities(
    sourceMedia,
    sourceStream,
    encodingStreams,
    encodingPlan,
  );

  if (sourceMedia && currentMedia.audio_only && sourceStream.type === "video")
    return false;

  if (
    sourceMedia &&
    sourceMedia.status === "available" &&
    ((qualitiesAvg && qualitiesAvg.length > 0) ||
      sourceStream.type === VTT_FILE_TYPES.SUBTITLES ||
      sourceStream.type === VTT_FILE_TYPES.CHAPTERS)
  )
    return true;

  return false;
};

export const secondsToTime = (secs, compact) => {
  let prefix = "";

  if (secs < 0) {
    secs = Math.abs(secs);
    prefix = "-";
  }

  const hours = Math.floor(secs / 3600);
  const minutes = Math.floor((secs - hours * 3600) / 60);
  const seconds = Math.floor(secs - hours * 3600 - minutes * 60);

  let hoursStr = hours;
  let minutesStr = minutes;
  let secondsStr = seconds;

  if (hours < 10) {
    hoursStr = `0${hours}`;
  }
  if (minutes < 10) {
    minutesStr = `0${minutes}`;
  }
  if (seconds < 10) {
    secondsStr = `0${seconds}`;
  }

  if (hours <= 0) return `${prefix}${minutesStr}m${secondsStr}s`;

  if (hours <= 0 && minutes <= 0) return `${prefix}${secondsStr}s`;

  if (compact) {
    if (hours > 0) return `${prefix}${hoursStr}h${minutesStr}m`;
  } else {
    return `${prefix}${hoursStr}h${minutesStr}m${secondsStr}s`;
  }
};

export const getQuotaCoeff = (quality, type) => {
  if (type === "video") return getQuotaCoeffVideo(quality);
  return getQuotaCoeffAudio(quality);
};

export const getQuotaCoeffVideo = (quality) => {
  switch (quality) {
    case "2160":
      return 1;
    case "1080":
      return 0.5;
    case "720":
      return 0.25;
    case "576":
    case "480":
    case "360":
    case "320":
    case "160":
      return 0.125;
    default:
      return 0.125;
  }
};

export const getQuotaCoeffAudio = (quality) => {
  switch (quality) {
    case "320":
    case "160":
      return 0.125;
    default:
      return 0.125;
  }
};

const getQuotaCoeffByProfile = (quality, qualities) => {
  const audioQuota = 0.25;
  let coeffLive = 0;

  qualities.some((qual) => {
    coeffLive += qual.value;
    if (qual.label === quality) return true;
  });

  coeffLive += audioQuota;

  return coeffLive;
};

const getCurrentQuota = (encodingStreams, qualities) => {
  let quotaCurrent = 0;

  encodingStreams.forEach((stream) => {
    if (stream.type === "video" || stream.type === "audio") {
      if (!stream.uuid) {
        const quotaCoeff = qualities
          ? getQuotaCoeffByProfile(stream.quality, qualities)
          : getQuotaCoeff(stream.quality, stream.type);

        quotaCurrent += parseInt(stream.duration) * quotaCoeff;
      }
    }
  });

  return quotaCurrent;
};

const formatQuotaPercent = (value) =>
  value % 1 === 0 ? value : value.toFixed(1);

export const getComputedQuotas = (encodingStreams, encodingPlan, qualities) => {
  const { encoding_sec = 0, max_encoding_sec = 0 } = encodingPlan || {};

  const quotaCurrent = getCurrentQuota(encodingStreams, qualities);

  const quotaUsed = encoding_sec + quotaCurrent;
  const quotaAvb = max_encoding_sec - quotaUsed;

  const quotaCurrentPercent =
    quotaAvb === 0 ? 0 : formatQuotaPercent((quotaCurrent / quotaAvb) * 100);
  const quotaUsedPercent =
    max_encoding_sec > 0
      ? formatQuotaPercent((quotaUsed / max_encoding_sec) * 100)
      : 0;
  const quotaAvbPercent =
    max_encoding_sec > 0
      ? formatQuotaPercent((quotaAvb / max_encoding_sec) * 100)
      : 0;

  return {
    used: {
      seconds: quotaUsed,
      percent: quotaUsedPercent,
    },
    current: {
      seconds: quotaCurrent,
      percent: quotaCurrentPercent,
    },
    available: {
      seconds: quotaAvb,
      percent: quotaAvbPercent,
    },
  };
};

export const getLiveComputedQuotas = (live, encodingPlan, qualities) =>
  getComputedQuotas(live, encodingPlan, qualities);

export const getQuotas = ({ stream, encodingPlan, liveEncodingPlan }) => {
  const qualities =
    liveEncodingPlan === "standard"
      ? [
          { label: "360", value: 0.25 },
          { label: "720", value: 0.5 },
          { label: "1080", value: 1 },
        ]
      : [
          { label: "360", value: 0.25 },
          { label: "576", value: 0.25 },
          { label: "720", value: 0.5 },
          { label: "1080", value: 1 },
          { label: "4096", value: 2 },
        ];

  return getLiveComputedQuotas([stream], encodingPlan, qualities);
};

/**
 * Here we generate encodingStreams array from hostedStreams obtained from api.
 * It's important to safe stream names from old encodingStreams array because
 * the update stream api is not takes a while to update hostedStreams.
 * So when the name of stream is changed, the hostedStreams array will contain
 * the old version of the name for a while which is not acceptable by the algorithm
 * of name dedublication.
 * @param {*} hostedStreams
 * @param {*} oldEncodingStreams
 * @param {*} media
 */
export const processStreams = (
  hostedStreams,
  oldEncodingStreams = [],
  media,
) => {
  const encodingStreams = [];

  const oldStreamsMap = {};
  oldEncodingStreams.forEach((stream) => {
    oldStreamsMap[stream.uuid] = stream;
  });

  if (
    hostedStreams.indexes &&
    hostedStreams.indexes.media_uuid &&
    hostedStreams.indexes.media_uuid[media.uuid] &&
    hostedStreams.indexes.media_uuid[media.uuid].length > 0
  ) {
    hostedStreams.indexes.media_uuid[media.uuid].forEach(
      (hostedStreamIndex) => {
        const stream = hostedStreams.data[hostedStreamIndex];
        const { uuid } = stream;
        if (oldStreamsMap[uuid]) {
          stream.name = oldStreamsMap[uuid].name;
        }
        encodingStreams.push({
          ...stream,
          key: uuid,
        });
      },
    );
  }

  return encodingStreams;
};
